import {
  CircleAlert,
  CircleCheck,
  FileUp,
  LoaderCircle,
  Upload,
  X,
} from "lucide-react";
import { useRevalidate } from "@/lib/loader";
import { useNavigate } from "react-router";
import { useState } from "react";
import { importWorkbook } from "@/lib/api/endpoints";
import { ImportWizard } from "@/components/import/ImportWizard";
import type { ImportConfig } from "@/lib/mapper/types";
import type {
  ImportMismatch,
  ImportWorkbookResponse,
} from "@/lib/api/types";

export type ImportMode = "replace-source" | "new";

export type ImportSummary = {
  tone: "success" | "warning";
  title: string;
  lines: string[];
};

type Props = {
  workbookId: string;
  sheet: string;
  onImported?: () => void;
};

function listText(values: readonly string[]): string {
  return values.join(", ");
}

function mismatchArray(
  mismatches: ImportMismatch | undefined,
  key: keyof ImportMismatch,
): string[] {
  return mismatches?.[key] ?? [];
}

export function hasImportWarnings(result: ImportWorkbookResponse): boolean {
  return (
    mismatchArray(result.mismatches, "removedSourceColumns").length > 0 ||
    mismatchArray(result.mismatches, "newSourceColumns").length > 0 ||
    mismatchArray(result.mismatches, "templateRefsBroken").length > 0 ||
    (result.droppedOverrideRowIds?.length ?? 0) > 0
  );
}

export function buildImportSummary(
  result: ImportWorkbookResponse,
): ImportSummary {
  const lines: string[] = [];
  const removed = mismatchArray(result.mismatches, "removedSourceColumns");
  const added = mismatchArray(result.mismatches, "newSourceColumns");
  const brokenRefs = mismatchArray(result.mismatches, "templateRefsBroken");
  const droppedCount = result.droppedOverrideRowIds?.length ?? 0;

  if (removed.length > 0) {
    lines.push(`Removed columns: ${listText(removed)}.`);
  }
  if (added.length > 0) {
    lines.push(`New columns: ${listText(added)}.`);
  }
  if (brokenRefs.length > 0) {
    lines.push(
      `Template references no longer found: ${listText(brokenRefs)}.`,
    );
  }
  if (droppedCount > 0) {
    lines.push(
      `${droppedCount} manual cell edit${
        droppedCount === 1 ? " was" : "s were"
      } dropped because their rows changed.`,
    );
  }

  if (lines.length === 0) {
    lines.push("Source columns matched the existing template.");
  }

  return {
    tone: hasImportWarnings(result) ? "warning" : "success",
    title: "Imported.",
    lines,
  };
}

export async function submitImportDialog(args: {
  id: string;
  sheet: string;
  mode: ImportMode;
  file: File;
  configs?: Record<string, ImportConfig>;
  navigate: (href: string) => void;
}): Promise<ImportWorkbookResponse> {
  const result = await importWorkbook({
    id: args.id,
    sheet: args.sheet,
    mode: args.mode,
    file: args.file,
    configs: args.configs,
  });

  if (args.mode === "new") {
    if (!result.workbookId) {
      throw new Error("Import completed without a new workbook id");
    }
    args.navigate(`/workbooks/${result.workbookId}`);
  }

  return result;
}

export function ImportSummaryPanel({ summary }: { summary: ImportSummary }) {
  return (
    <div
      role="status"
      className={[
        "rounded-md border px-3 py-3 text-sm",
        summary.tone === "warning"
          ? "border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/35 dark:text-amber-300"
          : "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900/60 dark:bg-emerald-950/35 dark:text-emerald-300",
      ].join(" ")}
    >
      <div className="flex items-center gap-2 font-semibold">
        {summary.tone === "warning" ? (
          <CircleAlert className="h-4 w-4" />
        ) : (
          <CircleCheck className="h-4 w-4" />
        )}
        {summary.title}
      </div>
      <ul className="mt-2 space-y-1">
        {summary.lines.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
    </div>
  );
}

export function ImportDialog({ workbookId, sheet, onImported }: Props) {
  const navigate = useNavigate();
  const revalidate = useRevalidate();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<ImportMode>("replace-source");
  const [file, setFile] = useState<File | null>(null);
  const [wizardFile, setWizardFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [summary, setSummary] = useState<ImportSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  function close() {
    if (submitting) return;
    setOpen(false);
    setError(null);
    setSummary(null);
    setWizardFile(null);
  }

  async function doSubmit(configs: Record<string, ImportConfig>) {
    if (submitting || !file) return;

    setSubmitting(true);
    setError(null);
    setSummary(null);
    try {
      const result = await submitImportDialog({
        id: workbookId,
        sheet,
        mode,
        file,
        configs,
        navigate: (href) => navigate(href),
      });
      if (mode === "replace-source") {
        setSummary(buildImportSummary(result));
        onImported?.();
        revalidate();
        setSubmitting(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Import failed");
      setSubmitting(false);
    }
  }

  function handleSubmit() {
    if (submitting) return;
    if (!file) {
      setError("Choose a CSV or Excel file first.");
      return;
    }
    // Open the wizard; the actual submit happens in onConfirm
    setWizardFile(file);
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Import source data"
        className="inline-flex items-center gap-1 rounded border border-slate-300 bg-white px-2 py-1.5 text-[12px] font-medium text-slate-700 transition hover:bg-slate-50 dark:border-border dark:bg-surface dark:text-slate-300 dark:hover:bg-surface-2"
      >
        <Upload className="h-3.5 w-3.5" />
        Import
      </button>

      {open && !wizardFile && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="import-workbook-title"
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/35 px-4 py-6 backdrop-blur-sm"
        >
          <button
            type="button"
            aria-label="Close import dialog"
            className="absolute inset-0 cursor-default"
            onClick={close}
          />
          <div className="relative w-full max-w-xl overflow-hidden rounded-[10px] border border-slate-200 bg-white shadow-xl dark:border-border dark:bg-surface">
            <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-5 py-4 dark:border-border">
              <div>
                <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                  {sheet}
                </p>
                <h2
                  id="import-workbook-title"
                  className="mt-1 text-lg font-semibold text-slate-950 dark:text-slate-100"
                >
                  Import
                </h2>
              </div>
              <button
                type="button"
                onClick={close}
                disabled={submitting}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50 dark:text-slate-400 dark:hover:bg-surface-2 dark:hover:text-slate-100"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4 px-5 py-5">
              <label className="block rounded-[10px] border border-dashed border-slate-300 bg-slate-50 px-4 py-4 transition hover:border-slate-400 dark:border-border dark:bg-background/60 dark:hover:border-slate-600">
                <span className="flex items-center gap-2 text-sm font-medium text-slate-800 dark:text-slate-200">
                  <FileUp className="h-4 w-4" />
                  Source file
                </span>
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  disabled={submitting}
                  onChange={(event) => {
                    const nextFile = event.currentTarget.files?.[0] ?? null;
                    setFile(nextFile);
                    setError(null);
                    setSummary(null);
                  }}
                  className="mt-3 block w-full text-sm text-slate-700 file:mr-3 file:rounded-md file:border-0 file:bg-slate-900 file:px-3 file:py-2 file:text-sm file:font-medium file:text-white hover:file:bg-slate-700 disabled:cursor-not-allowed dark:text-slate-300 dark:file:bg-slate-100 dark:file:text-slate-950 dark:hover:file:bg-slate-200"
                />
                <span className="mt-2 block truncate text-xs text-slate-500 dark:text-slate-400">
                  {file ? file.name : "CSV, XLSX, or XLS"}
                </span>
              </label>

              <div className="grid gap-2 sm:grid-cols-2" role="group">
                <button
                  type="button"
                  onClick={() => {
                    setMode("replace-source");
                    setSummary(null);
                  }}
                  disabled={submitting}
                  aria-pressed={mode === "replace-source"}
                  className={[
                    "rounded-md border px-3 py-3 text-left transition disabled:cursor-not-allowed disabled:opacity-60",
                    mode === "replace-source"
                      ? "border-slate-900 bg-slate-900 text-white dark:border-slate-100 dark:bg-slate-100 dark:text-slate-950"
                      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-border dark:bg-surface dark:text-slate-300 dark:hover:bg-surface-2",
                  ].join(" ")}
                >
                  <span className="block text-sm font-semibold">
                    Replace source data
                  </span>
                  <span className="mt-1 block text-xs opacity-75">
                    Keep this workbook.
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setMode("new");
                    setSummary(null);
                  }}
                  disabled={submitting}
                  aria-pressed={mode === "new"}
                  className={[
                    "rounded-md border px-3 py-3 text-left transition disabled:cursor-not-allowed disabled:opacity-60",
                    mode === "new"
                      ? "border-slate-900 bg-slate-900 text-white dark:border-slate-100 dark:bg-slate-100 dark:text-slate-950"
                      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-border dark:bg-surface dark:text-slate-300 dark:hover:bg-surface-2",
                  ].join(" ")}
                >
                  <span className="block text-sm font-semibold">
                    Create new workbook
                  </span>
                  <span className="mt-1 block text-xs opacity-75">
                    Leave this workbook unchanged.
                  </span>
                </button>
              </div>

              {summary && <ImportSummaryPanel summary={summary} />}

              {error && (
                <div
                  role="alert"
                  className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/70 dark:bg-red-950/35 dark:text-red-300"
                >
                  {error}
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-slate-100 px-5 py-4 dark:border-border">
              <button
                type="button"
                onClick={close}
                disabled={submitting}
                className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-border dark:bg-surface dark:text-slate-300 dark:hover:bg-surface-2"
              >
                Close
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting || !file}
                className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-slate-100 dark:text-slate-950 dark:hover:bg-slate-200"
              >
                {submitting && <LoaderCircle className="h-4 w-4 animate-spin" />}
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import wizard — shown after file is confirmed, before actual submit */}
      {open && wizardFile && (
        <ImportWizard
          file={wizardFile}
          mode="create"
          onConfirm={(configs) => {
            setWizardFile(null);
            void doSubmit(configs);
          }}
          onCancel={() => {
            setWizardFile(null);
          }}
        />
      )}
    </>
  );
}
